import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Appearance = "auto" | "light" | "dark" | "dim" | "contrast";
export type Density = "comfortable" | "compact";
export type TextSize = "small" | "medium" | "large";
export const ACCENT_PRESETS = [
  "indigo",
  "blue",
  "violet",
  "pink",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "gray",
] as const;
export type AccentPreset = (typeof ACCENT_PRESETS)[number] | "custom";

interface ThemePrefs {
  appearance: Appearance;
  accent: AccentPreset;
  customAccentHex: string | null;
  density: Density;
  textSize: TextSize;
}

const DEFAULT_PREFS: ThemePrefs = {
  appearance: "auto",
  accent: "indigo",
  customAccentHex: null,
  density: "comfortable",
  textSize: "medium",
};

const STORAGE_KEY = "reportforge.theme";

interface ThemeContextValue extends ThemePrefs {
  setAppearance: (v: Appearance) => void;
  setAccent: (v: AccentPreset) => void;
  setCustomAccentHex: (v: string) => void;
  setDensity: (v: Density) => void;
  setTextSize: (v: TextSize) => void;
  /** Resolves "auto" against the OS preference - for components (like Monaco, which manages its
   *  own theme independent of CSS) that need a plain yes/no rather than the raw preference. */
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredPrefs(): ThemePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<ThemePrefs>(() => readStoredPrefs());
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (prefs.appearance === "auto") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", prefs.appearance);
    }

    if (prefs.accent === "custom" && prefs.customAccentHex) {
      root.removeAttribute("data-accent");
      root.style.setProperty("--color-primary", prefs.customAccentHex);
      root.style.setProperty(
        "--color-primary-dark",
        `color-mix(in srgb, ${prefs.customAccentHex} 80%, black)`
      );
    } else {
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-primary-dark");
      root.setAttribute("data-accent", prefs.accent);
    }

    if (prefs.density === "compact") {
      root.setAttribute("data-density", "compact");
    } else {
      root.removeAttribute("data-density");
    }

    if (prefs.textSize !== "medium") {
      root.setAttribute("data-text-size", prefs.textSize);
    } else {
      root.removeAttribute("data-text-size");
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Private window / storage disabled - theme just won't persist across reloads.
    }
  }, [prefs]);

  const setAppearance = useCallback((appearance: Appearance) => setPrefs((p) => ({ ...p, appearance })), []);
  const setAccent = useCallback((accent: AccentPreset) => setPrefs((p) => ({ ...p, accent })), []);
  const setCustomAccentHex = useCallback(
    (customAccentHex: string) => setPrefs((p) => ({ ...p, accent: "custom", customAccentHex })),
    []
  );
  const setDensity = useCallback((density: Density) => setPrefs((p) => ({ ...p, density })), []);
  const setTextSize = useCallback((textSize: TextSize) => setPrefs((p) => ({ ...p, textSize })), []);

  const isDark =
    prefs.appearance === "auto" ? systemPrefersDark : prefs.appearance !== "light";

  const value = useMemo<ThemeContextValue>(
    () => ({ ...prefs, setAppearance, setAccent, setCustomAccentHex, setDensity, setTextSize, isDark }),
    [prefs, setAppearance, setAccent, setCustomAccentHex, setDensity, setTextSize, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
